import React from 'react';
import { MapPin, Mail } from 'lucide-react';

const Footer = ({ galleryInfo, siteSettings }) => {
    return (
        <footer className="bg-gray-900 border-t-4 border-orange-500 py-8 sm:py-12 md:py-16 text-gray-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Grid Layout - Responsive */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-10 mb-8 sm:mb-12">
                    
                    {/* Logo & Tagline */}
                    <div className="sm:col-span-1">
                        {galleryInfo && galleryInfo.logoUrl && galleryInfo.logoUrl.includes('https://') ?
                            <img 
                                className="h-14 sm:h-16 md:h-20 mb-3 sm:mb-4" 
                                src={galleryInfo.logoUrl} 
                                alt="Kolinsky Art Gallery Logo"
                                onError={(e) => e.target.style.display='none'}
                            />
                        : <div className="h-14 sm:h-16 md:h-20 w-14 sm:w-16 md:w-20 bg-orange-500 rounded-full mb-3 sm:mb-4"></div>}
                        <p className="text-xs sm:text-sm text-gray-400">Curating moments of inspiration and beauty.</p>
                    </div>
                    
                    {/* Locations */}
                    <div className="sm:col-span-1">
                        <h4 className="font-semibold text-white mb-3 sm:mb-4 text-sm md:text-lg">Locations</h4>
                        <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                            <p className="flex items-start gap-2">
                                <MapPin size={14} className="flex-shrink-0 text-orange-500 mt-0.5" />
                                <span className="text-gray-400">{galleryInfo && galleryInfo.addressUnawatuna ? galleryInfo.addressUnawatuna : 'Unawatuna, Sri Lanka'}</span>
                            </p>
                            <p className="flex items-start gap-2">
                                <MapPin size={14} className="flex-shrink-0 text-orange-500 mt-0.5" />
                                <span className="text-gray-400">{galleryInfo && galleryInfo.addressMirissa ? galleryInfo.addressMirissa : 'Mirissa, Sri Lanka'}</span>
                            </p>
                            <p className="flex items-start gap-2">
                                <MapPin size={14} className="flex-shrink-0 text-orange-500 mt-0.5" />
                                <span className="text-gray-400">Ahangama, Sri Lanka</span>
                            </p>
                        </div>
                    </div>
                    
                    {/* Sitemap */}
                    <div className="sm:col-span-1">
                        <h4 className="font-semibold text-white mb-3 sm:mb-4 text-sm md:text-lg">Sitemap</h4>
                        <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                            <li><a href="/" className="text-gray-400 hover:text-orange-400 transition">Home</a></li>
                            <li><a href="/gallery" className="text-gray-400 hover:text-orange-400 transition">Gallery</a></li>
                            <li><a href="/our-work" className="text-gray-400 hover:text-orange-400 transition">Our Work</a></li>
                            <li><a href="/about" className="text-gray-400 hover:text-orange-400 transition">About Us</a></li>
                            <li><a href="/contact" className="text-gray-400 hover:text-orange-400 transition">Contact</a></li>
                        </ul>
                    </div>
                    
                    {/* Connect */}
                    <div className="sm:col-span-1">
                        <h4 className="font-semibold text-white mb-3 sm:mb-4 text-sm md:text-lg">Connect</h4>
                        <div className="flex gap-3 sm:gap-4 items-center mb-4">
                            {siteSettings && siteSettings.whatsappLink && (
                                <a 
                                    href={siteSettings.whatsappLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    aria-label="WhatsApp" 
                                    className="hover:text-green-400 transition"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>
                                </a>
                            )}
                            {siteSettings && siteSettings.instagramLink && (
                                <a 
                                    href={siteSettings.instagramLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    aria-label="Instagram" 
                                    className="hover:text-orange-400 transition"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
                                </a>
                            )}
                            {siteSettings && siteSettings.facebookLink && (
                                <a 
                                    href={siteSettings.facebookLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    aria-label="Facebook" 
                                    className="hover:text-blue-400 transition"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                                </a>
                            )}
                            {siteSettings && siteSettings.emailAddress && (
                                <a 
                                    href={`mailto:${siteSettings.emailAddress}`} 
                                    aria-label="Email" 
                                    className="hover:text-orange-400 transition"
                                >
                                    <Mail size={20} />
                                </a>
                            )}
                        </div>
                        <p className="mt-4 text-xs text-gray-500">&copy; {new Date().getFullYear()} Kolinsky Art Gallery.</p>
                    </div>
                </div>

                {/* Bottom Border */}
                <div className="border-t border-gray-800 pt-6 sm:pt-8 text-center text-xs text-gray-500">
                    <p>All rights reserved. Designed with ♥ for art lovers.</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;